import { tv } from "tailwind-variants";

import { analytics } from "@/config/analytics";
import { useCareplanOrder } from "@/domains/careplan/hooks/use-careplan-order";
import type { CareplanProductDataModel } from "@/domains/careplan/models/types";
import { useCartStore } from "@/stores";
import { Button, Card, Heading, icons } from "@/ui";

const PERKS = ["Discounted Products", "30-Day Guarantee", "Cancel Anytime"];

const careplanSummaryCardVariants = tv({
  slots: {
    /* Same padding as every other care plan card (CareplanProduct, OngoingSupportCard). */
    root: "flex flex-col gap-[18px] px-5 py-6 sm:gap-6 sm:px-10 sm:py-10",
    heading: "text-[22px] leading-tight sm:text-[26px]",
    list: "flex flex-col divide-y divide-line overflow-hidden rounded-xl border border-line",
    /* One row per product. On desktop the "For Your ..." reason gets a second column. */
    /* 12px insets on a phone: the card already pads 20px, and the reason line should stay on one line at 375px. */
    row: "flex items-center gap-3 px-3 py-3.5 sm:grid sm:grid-cols-2 sm:items-stretch sm:gap-0 sm:px-0 sm:py-0",
    product: "flex min-w-0 items-center gap-3.5 sm:px-4 sm:py-3.5",
    image: "size-11 shrink-0 rounded-lg bg-product-media object-contain p-1",
    name: "min-w-0 text-lg leading-snug font-semibold text-content sm:text-xl",
    reason: "text-base leading-snug text-content-secondary sm:hidden",
    reasonColumn:
      "hidden min-w-0 items-center border-l border-line px-4 py-3.5 text-lg text-content-secondary sm:flex",
    actions: "flex flex-col gap-2.5",
    perks:
      "mt-1.5 flex flex-col gap-1 text-base text-content-secondary sm:mt-0 sm:flex-row sm:flex-wrap sm:justify-center sm:gap-x-6 sm:gap-y-2",
    perk: "flex items-center gap-2 sm:whitespace-nowrap",
  },
  variants: {
    removed: {
      true: {
        image: "opacity-35",
        name: "text-content-secondary",
        reason: "text-content-subtle",
        reasonColumn: "text-content-subtle",
      },
    },
  },
});

const {
  actions,
  heading,
  image,
  list,
  name,
  perk,
  perks,
  product,
  reason,
  reasonColumn,
  root,
  row,
} = careplanSummaryCardVariants();

/*
  The row thumbnail is meant to be a simpler cutout than the product card's photography. It
  comes from a Shopify product metafield that is not set up yet, so until a product carries one
  the row shows the same main image the product card uses further down the page.
*/
const getSummaryImage = (item: CareplanProductDataModel) => {
  return item.summaryImage ?? item.images[0];
};

type CareplanSummaryCardProps = {
  /** Where "See Product & Plan Details" scrolls to: the first product card, once it has mounted. */
  detailsElement?: HTMLElement | null;
};

export const CareplanSummaryCard = ({ detailsElement }: CareplanSummaryCardProps) => {
  const { order, products } = useCareplanOrder();
  const openCart = useCartStore((state) => {
    return state.openCart;
  });

  const handleSeeDetails = () => {
    analytics.track("careplan_summary_details_clicked");

    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    detailsElement?.scrollIntoView({
      behavior: prefersReducedMotion ? "auto" : "smooth",
      block: "start",
    });
    /* Focus follows the scroll, so the next Tab continues inside the card rather than up here. */
    detailsElement?.focus({ preventScroll: true });
  };

  const handleViewCart = () => {
    analytics.track("careplan_summary_view_cart_clicked");
    openCart();
  };

  return (
    <Card className={root()}>
      <Heading className={heading()} level={2}>
        Your Products
      </Heading>

      <ul className={list()}>
        {products.map((item) => {
          const line = order?.lines.find((orderLine) => {
            return orderLine.variantId === item.variantId;
          });
          const removed = !!line && !line.isActive;
          const reasonText = removed ? "Not in your order" : item.whyRecommended?.title;

          return (
            <li className={row()} key={item.id}>
              <div className={product()}>
                <img alt="" className={image({ removed })} src={getSummaryImage(item)} />
                <div className="flex min-w-0 flex-col">
                  <p className={name({ removed })}>{item.name}</p>
                  {reasonText ? <p className={reason({ removed })}>{reasonText}</p> : null}
                </div>
              </div>
              <p className={reasonColumn({ removed })}>{reasonText}</p>
            </li>
          );
        })}
      </ul>

      <div className={actions()}>
        <Button className="w-full" onClick={handleSeeDetails} size="lg">
          See Product & Plan Details
        </Button>
        {/* Narrower side padding on a phone so the label stays on one line at 375px. */}
        <Button
          className="w-full px-4 sm:px-7"
          onClick={handleViewCart}
          size="lg"
          variant="secondary-dark"
        >
          View/Edit Cart & Check Out
        </Button>
      </div>

      <ul className={perks()}>
        {PERKS.map((text) => {
          return (
            <li className={perk()} key={text}>
              <icons.CheckCircleIcon
                aria-hidden="true"
                className="size-[18px] shrink-0 text-success"
              />
              <span>{text}</span>
            </li>
          );
        })}
      </ul>
    </Card>
  );
};
